import Phaser from "phaser";
import Catboy from "../entities/Catboy";
import MonsterGirl from "../entities/MonsterGirl";
import BattlePair from "../entities/BattlePair";
import Projectile from "../entities/Projectile";
import HPBox from "../ui/HPBox";
import DialogBox from "../ui/DialogBox";
import ActionMenu, { type MenuItem } from "../ui/ActionMenu";
import { PLAYER_PAIR, ENEMY_PAIR } from "../data/pairs";
import { MOVES, MOVE_BY_ID } from "../data/moves";
import { rollDamage, rollHit } from "../systems/DamageCalc";
import { AIController } from "../systems/AIController";
import { BattleStateMachine } from "../systems/BattleStateMachine";
import { BattleState, type Move } from "../types";

const SCREEN_W = 480;
const SCREEN_H = 270;

const PLATFORM_PLAYER_X = 90;
const PLATFORM_PLAYER_Y = 200;
const PLATFORM_ENEMY_X = 360;
const PLATFORM_ENEMY_Y = 100;

const PLAYER_GIRL_OFFSET = { x: -10, y: -2 };
const PLAYER_CATBOY_OFFSET = { x: 22, y: 0 };
const ENEMY_GIRL_OFFSET = { x: 6, y: -2 };
const ENEMY_CATBOY_OFFSET = { x: -22, y: 0 };

interface TurnPlan {
  side: "player" | "enemy";
  move: Move;
  attacker: BattlePair;
  defender: BattlePair;
  attackerHp: HPBox;
  defenderHp: HPBox;
  /** В какую сторону летит снаряд: координаты атакующей и защищающейся платформ. */
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  /** Куда убегать при KO. */
  escapeDir: 1 | -1;
}

export default class BattleScene extends Phaser.Scene {
  private fsm = new BattleStateMachine();
  private ai = new AIController();

  private playerPair!: BattlePair;
  private enemyPair!: BattlePair;
  private playerHpBox!: HPBox;
  private enemyHpBox!: HPBox;
  private dialog!: DialogBox;
  private mainMenu!: ActionMenu;
  private moveMenu!: ActionMenu;

  constructor() {
    super("BattleScene");
  }

  create(): void {
    this.add.image(0, 0, "bg_arena").setOrigin(0, 0).setDepth(0);

    this.add.image(PLATFORM_ENEMY_X, PLATFORM_ENEMY_Y, "platform_enemy").setOrigin(0.5, 0.5).setDepth(1);
    this.add.image(PLATFORM_PLAYER_X, PLATFORM_PLAYER_Y, "platform_player").setOrigin(0.5, 0.5).setDepth(4);

    const enemyGirl = new MonsterGirl(
      this,
      PLATFORM_ENEMY_X + ENEMY_GIRL_OFFSET.x,
      PLATFORM_ENEMY_Y + ENEMY_GIRL_OFFSET.y,
      ENEMY_PAIR,
    );
    enemyGirl.setDepth(2);

    const enemyCatboy = new Catboy(
      this,
      PLATFORM_ENEMY_X + ENEMY_CATBOY_OFFSET.x,
      PLATFORM_ENEMY_Y + ENEMY_CATBOY_OFFSET.y,
      ENEMY_PAIR,
      "left",
    );
    enemyCatboy.setDepth(3);
    enemyCatboy.sprite.setScale(0.85);
    enemyGirl.sprite.setScale(0.7);

    const playerGirl = new MonsterGirl(
      this,
      PLATFORM_PLAYER_X + PLAYER_GIRL_OFFSET.x,
      PLATFORM_PLAYER_Y + PLAYER_GIRL_OFFSET.y,
      PLAYER_PAIR,
    );
    playerGirl.setDepth(5);

    const playerCatboy = new Catboy(
      this,
      PLATFORM_PLAYER_X + PLAYER_CATBOY_OFFSET.x,
      PLATFORM_PLAYER_Y + PLAYER_CATBOY_OFFSET.y,
      PLAYER_PAIR,
      "right",
    );
    playerCatboy.setDepth(6);

    this.playerPair = new BattlePair(this, PLAYER_PAIR, playerCatboy, playerGirl);
    this.enemyPair = new BattlePair(this, ENEMY_PAIR, enemyCatboy, enemyGirl);

    this.enemyHpBox = new HPBox(
      this,
      8,
      10,
      ENEMY_PAIR.catboyName,
      ENEMY_PAIR.girlName,
      ENEMY_PAIR.level,
      ENEMY_PAIR.maxHp,
      "enemy",
    );
    this.playerHpBox = new HPBox(
      this,
      SCREEN_W - 8 - 160,
      SCREEN_H - 70 - 50,
      PLAYER_PAIR.catboyName,
      PLAYER_PAIR.girlName,
      PLAYER_PAIR.level,
      PLAYER_PAIR.maxHp,
      "player",
    );

    this.dialog = new DialogBox(this, 0, SCREEN_H - 70);
    this.mainMenu = new ActionMenu(this, SCREEN_W - 200, SCREEN_H - 70, 2);
    this.moveMenu = new ActionMenu(this, SCREEN_W - 200, SCREEN_H - 70, 2);

    this.runBattle();
  }

  // === Главный цикл боя ===
  private async runBattle(): Promise<void> {
    await this.runIntro();

    while (true) {
      const playerMove = await this.selectPlayerMove();
      if (playerMove === null) {
        // Сбежать.
        this.scene.start("ResultScene", { victory: false });
        return;
      }

      const enemyMove = this.ai.pickMove();
      const order = this.resolveOrder(playerMove, enemyMove);

      for (const turn of order) {
        if (turn.attacker.isFainted() || turn.defender.isFainted()) continue;
        await this.executeTurn(turn);
      }

      // Эвазия — одноразовая на раунд: сбрасываем у обоих после раунда.
      this.playerPair.evadeBuff = 0;
      this.enemyPair.evadeBuff = 0;

      if (this.enemyPair.isFainted()) {
        await this.handleKO("enemy");
        return;
      }
      if (this.playerPair.isFainted()) {
        await this.handleKO("player");
        return;
      }
    }
  }

  // === Интро ===
  private async runIntro(): Promise<void> {
    this.fsm.transition(BattleState.Intro);

    await Projectile.playPuff(this, PLATFORM_ENEMY_X, PLATFORM_ENEMY_Y);
    await this.dialog.show(`${ENEMY_PAIR.catboyName} бросает вызов! Он выпускает ${ENEMY_PAIR.girlName}!`);
    await this.wait(200);

    await Projectile.playPuff(this, PLATFORM_PLAYER_X, PLATFORM_PLAYER_Y);
    await this.dialog.show(
      `Вперёд, ${PLAYER_PAIR.girlName}! ${PLAYER_PAIR.catboyName}, делай как она скажет!`,
    );
    await this.wait(150);
  }

  // === Выбор хода игроком ===
  /** Возвращает выбранный Move или null если игрок выбрал "Сбежать". */
  private async selectPlayerMove(): Promise<Move | null> {
    this.fsm.transition(BattleState.PlayerMenu);
    await this.dialog.show("Что прикажет Лилит?");

    const items: MenuItem[] = [
      { label: "Атака", payload: "dominate" },
      { label: "Подарок", payload: "gift", disabled: true },
      { label: "Смена", payload: "swap", disabled: true },
      { label: "Бежать", payload: "flee" },
    ];
    this.mainMenu.setItems(items);
    const picked = await this.mainMenu.open();

    if (picked.payload === "flee") {
      await this.dialog.show("Лилит закатывает глаза и улетает...");
      return null;
    }
    if (picked.payload === "dominate") {
      return this.selectMove();
    }
    return this.selectPlayerMove();
  }

  /** Меню атак с боксом описания слева (через DialogBox.setText). */
  private async selectMove(): Promise<Move> {
    this.fsm.transition(BattleState.PlayerCommandSelect);
    const items: MenuItem[] = MOVES.map((m) => ({
      label: m.name,
      payload: m.id,
    }));
    this.moveMenu.setItems(items);

    // Узкий wrap, чтобы текст описания не залезал под меню (200 px справа).
    const picked = await this.moveMenu.open((item) => {
      const m = MOVE_BY_ID[item.payload];
      if (!m) return;
      this.dialog.setText(`${m.name}\n${m.description}`, 260);
    });

    // Восстановить wrap для нормального диалога.
    this.dialog.setText("", 460);

    return MOVE_BY_ID[picked.payload];
  }

  // === Порядок ходов: priority → speed → tiebreak random ===
  private resolveOrder(playerMove: Move, enemyMove: Move): TurnPlan[] {
    const player: TurnPlan = {
      side: "player",
      move: playerMove,
      attacker: this.playerPair,
      defender: this.enemyPair,
      attackerHp: this.playerHpBox,
      defenderHp: this.enemyHpBox,
      fromX: PLATFORM_PLAYER_X,
      fromY: PLATFORM_PLAYER_Y - 30,
      toX: PLATFORM_ENEMY_X,
      toY: PLATFORM_ENEMY_Y - 30,
      escapeDir: -1,
    };
    const enemy: TurnPlan = {
      side: "enemy",
      move: enemyMove,
      attacker: this.enemyPair,
      defender: this.playerPair,
      attackerHp: this.enemyHpBox,
      defenderHp: this.playerHpBox,
      fromX: PLATFORM_ENEMY_X,
      fromY: PLATFORM_ENEMY_Y - 30,
      toX: PLATFORM_PLAYER_X,
      toY: PLATFORM_PLAYER_Y - 30,
      escapeDir: 1,
    };

    if (playerMove.priority !== enemyMove.priority) {
      return playerMove.priority > enemyMove.priority ? [player, enemy] : [enemy, player];
    }
    const sp = this.playerPair.data.speedStat;
    const se = this.enemyPair.data.speedStat;
    if (sp !== se) return sp > se ? [player, enemy] : [enemy, player];

    return Math.random() < 0.5 ? [player, enemy] : [enemy, player];
  }

  // === Исполнение одного хода ===
  private async executeTurn(turn: TurnPlan): Promise<void> {
    if (turn.side === "player") {
      this.fsm.transition(BattleState.PlayerActionAnim);
    } else {
      this.fsm.transition(BattleState.EnemyActionAnim);
    }

    const { move, attacker, defender, attackerHp } = turn;
    const girlName = attacker.data.girlName;
    const catboyName = attacker.data.catboyName;

    await this.dialog.show(`${girlName} командует ${catboyName}: ${move.name}!`);

    await attacker.girl.playCommand();
    await attacker.catboy.playPose(move);

    if (move.kind === "support") {
      // Self-target: heal + поставить эвазию на саму атакующую пару.
      if (move.evadeBuff && move.evadeBuff > 0) {
        attacker.evadeBuff = Math.max(attacker.evadeBuff, move.evadeBuff);
      }
      let healed = 0;
      if (move.heal && move.heal > 0) {
        healed = attacker.heal(move.heal);
        await attackerHp.updateHp(attacker.hp);
      }
      const parts: string[] = [];
      if (healed > 0) parts.push(`+${healed} HP`);
      if (move.evadeBuff) parts.push(`уворот +${Math.round(move.evadeBuff * 100)}%`);
      const tail = parts.length ? ` (${parts.join(", ")})` : "";
      await this.dialog.show(`${catboyName} зажмурился и собрался с духом${tail}.`);

      attacker.catboy.startIdle();
      attacker.girl.startIdle();
      return;
    }

    // Атака.
    const hit = rollHit(move, defender);
    if (!hit) {
      await this.dialog.show(`Но ${catboyName} ${move.flavorMiss}!`);
      attacker.catboy.startIdle();
      attacker.girl.startIdle();
      return;
    }

    const proj = new Projectile(this, turn.fromX, turn.fromY);
    await proj.fly(turn.toX, turn.toY, 450);
    await Projectile.playHitFx(this, turn.toX, turn.toY);

    await defender.playHurt();
    await defender.playBlushReact();

    const damage = rollDamage(move);
    const dealt = defender.takeDamage(damage);
    await turn.defenderHp.updateHp(defender.hp);

    await this.dialog.show(`${defender.data.catboyName} и ${defender.data.girlName} смущены! −${dealt} HP.`);

    await attacker.girl.playSmug(300);
    attacker.catboy.startIdle();
    attacker.girl.startIdle();
    if (!defender.isFainted()) {
      defender.catboy.startIdle();
      defender.girl.startIdle();
    }
  }

  // === KO ===
  private async handleKO(loser: "player" | "enemy"): Promise<void> {
    this.fsm.transition(BattleState.CheckKO);
    if (loser === "enemy") {
      await this.dialog.show(`${ENEMY_PAIR.catboyName} и ${ENEMY_PAIR.girlName} не выдерживают и убегают!`);
      await this.enemyPair.playFaintEscape(1, SCREEN_W);
    } else {
      await this.dialog.show(`${PLAYER_PAIR.catboyName} и ${PLAYER_PAIR.girlName} прячут лица и сбегают...`);
      await this.playerPair.playFaintEscape(-1, SCREEN_W);
    }

    this.fsm.transition(BattleState.BattleEnd);
    await this.wait(1500);
    this.scene.start("ResultScene", { victory: loser === "enemy" });
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => this.time.delayedCall(ms, () => resolve()));
  }
}
